import React from 'react';
import { ArrowRight, Settings, Trash2, Save, Copy } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

interface InstanceSummaryProps {
  instance: {
    instanceId: string;
    pluginId: string;
    version: string;
    configName: string;
    matcher?: string;
    excluder?: string;
    isPreview?: boolean;
  };
  pathRemap?: {
    from: string; // Plugin's internal path
    to: string; // External path (what users see)
  };
  onEditConfig?: () => void;
  onRemoveInstance?: () => void;
  onCommit?: () => void;
}

/**
 * Instance Summary Component
 *
 * Displays a visual representation of traffic flow:
 * [external path] → [matcher] except [excluder] → [internal path] → [instance box with ID tag]
 */
export function InstanceSummary({
  instance,
  pathRemap,
  onEditConfig,
  onRemoveInstance,
  onCommit,
}: InstanceSummaryProps) {
  // Copy instance ID to clipboard
  const handleCopyInstanceId = () => {
    navigator.clipboard.writeText(instance.instanceId);
    toast.success('Instance ID copied to clipboard');
  };

  return (
    <div className="flex items-center gap-2.5">
      {/* Path Remapping Flow: External → Matcher → Internal */}
      {pathRemap && (
        <>
          <Badge
            variant="outline"
            className="h-5 border-green-300 bg-green-50 text-[10px] font-medium text-green-700 dark:border-green-700 dark:bg-green-950/30 dark:text-green-400"
          >
            <code className="font-mono">{pathRemap.to}</code>
          </Badge>
          <ArrowRight className="text-muted-foreground/40 h-3 w-3 flex-shrink-0" />
        </>
      )}

      {/* Matcher/Excluder Pattern */}
      <div className="flex items-center gap-1.5">
        <code className="border-border/40 bg-muted/40 text-muted-foreground/80 rounded border px-1.5 py-0.5 text-[11px]">
          {instance.matcher || '.*'}
        </code>
        {instance.excluder && (
          <>
            <span className="text-muted-foreground/50 text-[10px]">!</span>
            <code className="border-destructive/20 bg-destructive/5 text-destructive/70 rounded border px-1.5 py-0.5 text-[11px]">
              {instance.excluder}
            </code>
          </>
        )}
      </div>

      {/* Arrow */}
      <ArrowRight className="text-muted-foreground/40 h-3 w-3 flex-shrink-0" />

      {/* Internal Path (what plugin receives) */}
      {pathRemap && (
        <>
          <Badge
            variant="outline"
            className="h-5 border-blue-300 bg-blue-50 text-[10px] font-medium text-blue-700 dark:border-blue-700 dark:bg-blue-950/30 dark:text-blue-400"
          >
            <code className="font-mono">{pathRemap.from}</code>
          </Badge>
          <ArrowRight className="text-muted-foreground/40 h-3 w-3 flex-shrink-0" />
        </>
      )}

      {/* Instance Info - Compact Box */}
      <div className="border-primary/20 bg-card group/instance relative rounded-md border shadow-sm">
        {/* Main content - Centered */}
        <div className="flex items-center gap-2 px-3 py-2">
          <span className="text-foreground text-xs font-semibold">{instance.pluginId}</span>
          <span className="text-muted-foreground/40 text-xs">•</span>
          <code className="text-foreground font-mono text-xs">v{instance.version}</code>
          <span className="text-muted-foreground/40 text-xs">•</span>
          <Badge
            variant={instance.isPreview ? 'outline' : 'secondary'}
            className={`h-4 text-[11px] ${
              instance.isPreview
                ? 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-700 dark:bg-orange-950/30 dark:text-orange-400'
                : ''
            }`}
          >
            {instance.isPreview && '🧪 '}
            {instance.configName}
          </Badge>

          {/* Action Buttons - Inline (always visible, background on hover) */}
          <div className="flex items-center gap-0.5">
            {onEditConfig && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onEditConfig}
                className="hover:bg-primary/10 h-6 w-6 p-0 transition-colors"
                title="Edit Config"
              >
                <Settings className="text-muted-foreground/60 hover:text-foreground h-3.5 w-3.5 transition-colors" />
              </Button>
            )}
            {onCommit && instance.isPreview && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onCommit}
                className="h-6 w-6 p-0 transition-colors hover:bg-green-100 dark:hover:bg-green-950"
                title="Commit to production"
              >
                <Save className="h-3.5 w-3.5 text-green-600 transition-colors hover:text-green-700 dark:text-green-500 dark:hover:text-green-400" />
              </Button>
            )}
            {onRemoveInstance && (
              <Button
                size="sm"
                variant="ghost"
                onClick={onRemoveInstance}
                className="hover:bg-destructive/10 h-6 w-6 p-0 transition-colors"
                title="Remove unused instance"
              >
                <Trash2 className="text-destructive/60 hover:text-destructive h-3.5 w-3.5 transition-colors" />
              </Button>
            )}
          </div>
        </div>

        {/* Instance ID - Absolute bottom (clickable to copy) */}
        <div
          className="absolute inset-x-0 bottom-0 flex cursor-pointer justify-start px-2 pb-0.5 transition-opacity hover:opacity-100"
          onClick={handleCopyInstanceId}
          title="Click to copy instance ID"
        >
          <code className="text-muted-foreground/50 hover:text-muted-foreground flex items-center gap-1 font-mono text-[8px] leading-none transition-colors">
            <Copy className="h-2 w-2" />
            {instance.instanceId}
          </code>
        </div>
      </div>
    </div>
  );
}
